import * as React from "react";
import { ChangeEvent, useState } from "react";
import "./CaregiverBasicDetailsScreen.css";
import { CaregiverCreateParams } from "../messages/Caregiver";
import PlacesAutocomplete, { geocodeByAddress } from "react-places-autocomplete";
import { LanguageMultiSelect } from "./widgets/LanguageMultiSelect";
import { isLoading, Loading, loading } from "../Loading";
import { Intent, Spinner } from "@blueprintjs/core";
import { LocalDate } from "js-joda";
import * as EXIF from "exif-js/exif";
import { AddressComponents, FormatedAddressDetails } from "../messages/Visit";
import { CaregiverCertification, OfficeId } from "../messages/ObjectIds";
import { Office } from "../messages/HR";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import { setCertificationActionCreator } from "src/store/slices/certificationsSlice";
import { AppState } from "src/store/store";
import { useHistory } from "react-router";
import { AddressDetails, fromAddressText } from "mf-google-geocoder";
import { AddressComponent, AddressType } from "@googlemaps/google-maps-services-js/dist/common";
interface CaregiverBasicDetailsScreenProps {
    phoneNumber: string;
    onSubmitClick: (params: CaregiverCreateParams, photoUrl: string | null) => void;
    showInternetError: boolean | Loading;
    createParams: CaregiverCreateParams;
    title: string;
    certificationsInProgress: CaregiverCertification[];
}

const CaregiverBasicDetailsScreen = (props: CaregiverBasicDetailsScreenProps) => {
    const [caregiverParams, setCaregiverParams] = useState<CaregiverCreateParams>(props.createParams);
    const [isAddressValid, setIsAddressValid] = useState<boolean>(props.createParams.addressGeoLocation !== { lat: 0, lng: 0 });
    const [photoUrl, setPhotoUrl] = useState<string | null>(props.createParams.photoUrl);
    const [errorMessage, setErrorMessage] = useState<string | null | Loading>(null);
    const [rotationAngle, setRotationAngle] = useState<number>(0);
    const [dimensions, setDimensions] = useState<{ x: number, y: number } | null>(null);
    const [exifRotate, setExifRotate] = useState<number>(1);
    const [geoSearchTerm, setGeoSearchTerm] = useState<string>(caregiverParams.address);
    const GOOGLE_MAPS_API_KEY = "AIzaSyAcpv_xCANE3_P2EEqNmZL4LESMR_iAIik";
    let isMounted = true;

    let offices: Office[] = useSelector((state: AppState) => state.offices.agencyOffices);
    let agencyCertifications: CaregiverCertification[] = useSelector((state: AppState) => state.certifications.agencyCertifications);
    const dispatch = useDispatch();


    const createAddressComponents = function (address: AddressDetails): AddressComponents {
        let addressComponentsCountry = null;
        let administrativeAreaLevel1 = null;
        let administrativeAreaLevel2 = null;
        let sublocalityLevel1 = null;
        let neighborhood = null;
        let locality = null;

        address.googleGeoCodeResponse.address_components.forEach(function (section: AddressComponent) {
            if (section.types.indexOf(AddressType.sublocality_level_1) > -1 || section.types.indexOf(AddressType.sublocality) > -1) {
                sublocalityLevel1 = section.short_name;
            }
            if (section.types.indexOf(AddressType.locality) > -1) {
                locality = section.short_name;
            }
            if (section.types.indexOf(AddressType.neighborhood) > -1) {
                neighborhood = section.short_name;
            }
            if (section.types.indexOf(AddressType.administrative_area_level_1) > -1) {
                administrativeAreaLevel1 = section.short_name;
            }
            if (section.types.indexOf(AddressType.administrative_area_level_2) > -1) {
                administrativeAreaLevel2 = section.short_name;
            }
            if (section.types.indexOf(AddressType.country) > -1) {
                addressComponentsCountry = section.short_name;
            }
        });
        const formatedAddress: FormatedAddressDetails = {
            location: { lat: address.location.lat, lng: address.location.lng },
            address1: (address.streetNumber === null ? "" : address.streetNumber + " ") + address.street,
            address2: address.address2,
            country: address.country || "",
            state: address.state || "",
            county: address.county || "",
            city: address.city,
            zip5: address.zip,
            zip4: address.zipSuffix,
            street: address.street,
            streetNumber: address.streetNumber,
            fullAddress: address.fullAddress
        };
        return {
            location: { lat: address.location.lat, lng: address.location.lng },
            country: addressComponentsCountry || "",
            administrativeAreaLevel1: administrativeAreaLevel1 || "",
            administrativeAreaLevel2: administrativeAreaLevel2 ? administrativeAreaLevel2 : administrativeAreaLevel1 || "",
            sublocalityLevel1: sublocalityLevel1,
            neighborhood: neighborhood,
            locality: locality,
            formatedAddressDetails: formatedAddress
        };
    };

    const setAddressGeoInfo = async () => {

        let geoInfo: AddressDetails;
        try {
            geoInfo = await fromAddressText(geoSearchTerm,
                { apiKey: GOOGLE_MAPS_API_KEY, mfAutoFix: true }, ["country", "state", "county"]);
        } catch (err) {
            setIsAddressValid(false);
            setErrorMessage("Invalid Address");
            return;
        }

        if (!isMounted) {
            return;
        }

        let addressComponents: any = createAddressComponents(geoInfo);
        let addressGeoLocation = { lat: geoInfo.location.lat, lng: geoInfo.location.lng };
        const formattedAddress = addressComponents.formatedAddressDetails.fullAddress;

        if (errorMessage === "Invalid Address") {
            setErrorMessage(null);
        }

        if (!isAddressValid) {
            setIsAddressValid(true);
        }

        return setCaregiverParams(prevState => ({
            ...prevState,
            address: formattedAddress,
            addressGeoLocation: addressGeoLocation,
            addressComponents: addressComponents
        }));

    };

    useEffect(() => {
        if (props.createParams.addressGeoLocation && !props.createParams.addressComponents) {
            setAddressGeoInfo();
        }
        return () => { isMounted = false; };
    }, [props.createParams.addressGeoLocation]);

    const handleSubmitClick = async () => {
        setErrorMessage(loading());
        if (caregiverParams.firstName.length === 0) {
            setErrorMessage("Missing first name");
        } else if (caregiverParams.lastName.length === 0) {
            setErrorMessage("Missing last name");
        } else if (caregiverParams.email && caregiverParams.email.length !== 0 && (caregiverParams.email.length < 5 || caregiverParams.email.indexOf("@") === -1 || caregiverParams.email.indexOf(".") === -1)) {
            setErrorMessage("Email is invalid");
        } else if (!caregiverParams.certification || CaregiverCertification.unwrap(caregiverParams.certification) === "") {
            setErrorMessage("Please choose a certification");
        } else if (caregiverParams.languages.length < 1) {
            setErrorMessage("Please choose a language");
        } else if (!isAddressValid) {
            setErrorMessage("Please choose an address from the list");
        } else if (caregiverParams.address.length === 0 || !isAddressValid) {
            setErrorMessage("Missing address");
        } else if (!caregiverParams.officeIds.length) {
            setErrorMessage("Please choose an office");
        } else {
            if (!caregiverParams.addressComponents || !caregiverParams.addressGeoLocation) {
                await setAddressGeoInfo();
            }
            dispatch(setCertificationActionCreator(caregiverParams.certification));
            props.onSubmitClick(caregiverParams, photoUrl);
            setErrorMessage(null);
        }
    };

    const handleAddressChange = async (address: string, isValid: boolean) => {
        setGeoSearchTerm(address);
        setCaregiverParams(prevState => ({ ...prevState, address: address }));
        setIsAddressValid(isValid);
        await setAddressGeoInfo();
    };

    const handleAddress2Change = (event: ChangeEvent<HTMLInputElement>) => {
        const val = event.target.value;
        setCaregiverParams(prevState => ({ ...prevState, address2: val }));

    };

    const handleFirstNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        const val = event.target.value;
        setCaregiverParams(prevState => ({ ...prevState, firstName: val }));

    };

    const handleLastNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        const val = event.target.value;
        setCaregiverParams(prevState => ({ ...prevState, lastName: val }));
    };

    const handleSelectCertChange = (event: ChangeEvent<HTMLSelectElement>) => {
        const val = CaregiverCertification.wrap(event.target.selectedOptions[0].value);
        setCaregiverParams({ ...caregiverParams, certification: val });
    };

    const handleSelectOfficeChange = (event: ChangeEvent<HTMLSelectElement>) => {
        const val = parseInt(event.target.value, 10);
        setCaregiverParams({ ...caregiverParams, officeIds: [OfficeId.wrap(val)] });
    };

    const handleLanguagesChange = (languages: string[]) => {
        setCaregiverParams({ ...caregiverParams, languages: languages });
    };

    const handlePhotoChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.files === null || event.target.files.length === 0) {
            setPhotoUrl(null);
            return;
        }
        const file = event.target.files[0];

        const readerForOrientation = new FileReader();
        const reader = new FileReader();
        readerForOrientation.onload = () => {
            const exif = EXIF.readFromBinaryFile(readerForOrientation.result);
            if (exif) {
                setDimensions({ x: exif.PixelXDimension, y: exif.PixelYDimension });
                switch (exif.Orientation) {
                    case 4:
                    case 8:
                        setExifRotate(8);
                        break;
                    case 7:
                    case 3:
                        setExifRotate(3);
                        break;
                    case 2:
                    case 6:
                        setExifRotate(6);
                        break;
                    case 5:
                    case 1: {
                        setExifRotate(1);
                        break;
                    }
                    default:
                        break;
                }
            } else {
                setExifRotate(1);
            }
            reader.readAsDataURL(file);
        };
        readerForOrientation.readAsArrayBuffer(file);
        const self = this;
        reader.onload = () => {
            setPhotoUrl((reader.result as any));
        };
    };

    const handleMiddleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        const middleName = event.target.value;
        setCaregiverParams({...caregiverParams, middleName: middleName});
    };

    const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
        const email = event.target.value;
        setCaregiverParams({...caregiverParams, email: email});
    };

    const handleBirthDateChange = (localDate: LocalDate | null) => {
        setCaregiverParams({...caregiverParams, birthDateDate: localDate});
    };

    /**
     * Get unique certification list
     */
    const getCertificationList = (agencyCertifications: CaregiverCertification[], caregiverCertification: CaregiverCertification | undefined): CaregiverCertification[] => {
        let arr = agencyCertifications;
        if (caregiverCertification) {
            arr = arr.concat(caregiverCertification);
        }
        return Array.from(new Set(arr));
    };

    return (
        <form className={"CaregiverBasicDetailsScreen__form"} onSubmit={e => e.preventDefault()}>
            <h4 className={"text-secondary mb-0"}>{props.title}</h4>
            <div className="control-group">
                <div className="GenericTextInput__container">
                    <label>First name</label>
                </div>
                <input
                    required={true}
                    className={"form-control"}
                    type={"text"}
                    onChange={(e) => handleFirstNameChange(e)}
                    placeholder={"First name"}
                    value={caregiverParams.firstName}
                />
            </div>
            <div className="control-group">
                <div className="GenericTextInput__container">
                    <label>Middle name</label>
                </div>
                <input
                    className={"form-control"}
                    type={"text"}
                    onChange={(e) => handleMiddleNameChange(e)}
                    placeholder={"Middle name"}
                    value={caregiverParams.middleName === null ? "" : caregiverParams.middleName}
                />
            </div>
            <div className="control-group">
                <div className="">
                    <label>Last name</label>
                </div>
                <input
                    required={true}
                    className={"form-control"}
                    type={"text"}
                    onChange={(e) => handleLastNameChange(e)}
                    placeholder={"Last name"}
                    value={caregiverParams.lastName}
                />
            </div>
            <div className="control-group">
                <div>
                    <label>Take a selfie (;</label>
                </div>
                <div className={"CaregiverBasicDetailsScreen__fileUploadContainer"}>
                    <input
                        type="file"
                        onChange={(e) => handlePhotoChange(e)}
                        accept="image/*"
                        capture={true}
                    />
                    {photoUrl !== null ?
                        <div
                            style={{
                                background: `url(${photoUrl}) center / cover`
                            }}
                            className={`CaregiverBasicDetailsScreen__fileUploadPreview CaregiverBasicDetailsScreen__exif${exifRotate}`}
                        />
                        : null}
                </div>
            </div>
            <div className="control-group">
                <div className="">
                    <label>Email</label>
                </div>
                <input
                    required={false}
                    className={"form-control"}
                    type={"email"}
                    onChange={(e) => handleEmailChange(e)}
                    placeholder={"Email"}
                    value={caregiverParams.email}
                />
            </div>
            <div className="control-group">
                <div className="">
                    <label>Certification</label>
                </div>
                <select
                    required={true}
                    className={"form-control"}
                    onChange={(e) => handleSelectCertChange(e)}
                    value={caregiverParams.certification ? CaregiverCertification.unwrap(caregiverParams.certification) : ""}
                >
                    <option value="" hidden={true} key="no-certification">Select</option>
                    {getCertificationList(agencyCertifications, caregiverParams.certification).map((cert, i) => {
                        return (
                            <option key={i} value={CaregiverCertification.unwrap(cert)}>
                                {cert}
                                {props.certificationsInProgress.length > 1 && props.certificationsInProgress.indexOf(cert) !== -1 ? " (in progress)" : null}
                            </option>
                        );
                    })}
                </select>
            </div>
            <div className="control-group">
                <div className="">
                    <label>Which languages do you speak?</label>
                </div>
                <div className={"CaregiverBasicDetailsScreen__LanguagesInput"}>
                    <LanguageMultiSelect
                        selectedLanguages={caregiverParams.languages}
                        onChange={handleLanguagesChange}
                    />
                </div>
            </div>
            <div className="control-group">
                <div className="GenericTextInput__container">
                    <label>Address</label>
                </div>
                <PlacesAutocomplete
                    value={geoSearchTerm}
                    onChange={val => {
                        handleAddressChange(val, false);
                    }}
                    onSelect={val => handleAddressChange(val, true)}
                >
                    {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                        <div>
                            <input
                                {...getInputProps({
                                    placeholder: "Address",
                                    className: "form-control"
                                })}
                            />
                            <div className="autocomplete-dropdown-container">
                                {loading && <div>Loading...</div>}
                                {suggestions.map(suggestion => {
                                    const className = suggestion.active
                                        ? "suggestion-item--active"
                                        : "suggestion-item";
                                    // inline style for demonstration purpose
                                    const style = suggestion.active
                                        ? { backgroundColor: "#fafafa", cursor: "pointer" }
                                        : { backgroundColor: "#ffffff", cursor: "pointer" };
                                    return (
                                        <div
                                            {...getSuggestionItemProps(suggestion, {
                                                className,
                                                style,
                                            })}
                                            key={suggestion.id}
                                        >
                                            <span>{suggestion.description}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </PlacesAutocomplete>
            </div>
            <div className="control-group">
                <div className="">
                    <label>Address line 2</label>
                </div>
                <input
                    required={false}
                    className={"form-control"}
                    type={"text"}
                    onChange={(e) => handleAddress2Change(e)}
                    placeholder={"Address 2"}
                    value={caregiverParams.address2 === null ? "" : caregiverParams.address2}
                />
            </div>
            {
                offices.length === 1 ? null :
                    <div className="control-group">
                        <div className="">
                            <label>Office</label>
                        </div>
                        <select
                            required={true}
                            className={"form-control"}
                            onChange={(e) => handleSelectOfficeChange(e)}
                            value={OfficeId.unwrap(caregiverParams.officeIds[0])}
                        >
                            <option value="" hidden={true} key="no-office">Select</option>
                            {offices.filter(office => office.active).map(office => {
                                return <option
                                    value={OfficeId.unwrap(office.id)}
                                    key={OfficeId.unwrap(office.id)}
                                >
                                    {office.name}
                                </option>;
                            })}

                        </select>
                    </div>
            }
            <h4 className={"help-block text-danger CaregiverBasicDetailsScreen__errorContainer"}>
                {props.showInternetError === true ? "Email already exists, use a different one" : null}
                {typeof errorMessage === "string" ? errorMessage : null}
            </h4>

            {isLoading(props.showInternetError) || isLoading(errorMessage) ?
                <div className={"CaregiverBasicDetailsScreen__loadingContainer"}>
                    <Spinner intent={Intent.PRIMARY} />
                </div>
                :
                <button
                    className="btn btn-primary btn-md CaregiverBasicDetailsScreen__button"
                    onClick={handleSubmitClick}
                >
                    Next!
                </button>

            }
        </form>
    );

};

export default CaregiverBasicDetailsScreen;



// WEBPACK FOOTER //
// ./src/components/CaregiverBasicDetailsScreen.tsx